import { z } from 'zod'
import type { AccommodationType } from '../../_kernel/Accommodation'
import { Accommodation } from '../../_kernel/Accommodation'
import { Address } from '../../_kernel/Address'
import { Model } from '../../_kernel/architecture'
import { City } from '../../_kernel/City'
import type { Countries } from '../../_kernel/Country'
import { Country } from '../../_kernel/Country'
import { DistanceToCityCenter } from '../../_kernel/DistanceToCityCenter'
import { GeoCoordinate } from '../../_kernel/GeoCoordenate'
import { ID } from '../../_kernel/ID'
import type { Image } from '../../_kernel/Image'
import { ListOfImage } from '../../_kernel/ListOfImage'
import { Name } from '../../_kernel/Name'
import { Nights } from '../../_kernel/Nights'
import { NumberOfComments } from '../../_kernel/NumberOfComments'
import { Price } from '../../_kernel/Price'
import { Rating } from '../../_kernel/Rating'
import { Slug } from '../../_kernel/Slug'
import { Stars } from '../../_kernel/Stars'
import type { Affiliate } from '../../shared/domain/Affiliate'
import type { Language } from '../../shared/domain/Language'
import type { Market } from '../../shared/domain/Market'
import type { Source } from '../../shared/domain/Source'
import type { POSTBannerResponseType } from '../infrastructure/HTTPPropertyRepository/schemas/banner'
import type { GETpropertiesByIDsResponse } from '../infrastructure/HTTPPropertyRepository/schemas/properties/ids'
import type { POSTRelatedResponseType } from '../infrastructure/HTTPPropertyRepository/schemas/related'
import type { SearchProperty } from '../infrastructure/HTTPPropertyRepository/schemas/v3/search/legacy'
import type { GetV3PropertyResponse } from '../infrastructure/HTTPPropertyRepository/schemas/v3/property/:country/:city/:slug'
import type { Facility } from '../../shared/domain/Facility'
import { ListOfFacility } from '../../shared/domain/ListOfFacility'
import { PropertyHighlighted } from './PropertyHighlighted'
import { PropertySource } from './PropertySource'
import type { PartialSearch } from '../../shared/domain/PartialSearch'

type NonEmptyArray<T> = [T, ...T[]]

export const PropertyValidation = z.object({
  id: z.instanceof(ID, { message: '[Property] id is required' }),
  name: z.instanceof(Name, { message: '[Property] name is required' }),
  country: z.instanceof(Country, { message: '[Property] country is required' }),
  slug: z.instanceof(Slug, { message: '[Property] slug is required' }),
  accommodation: z.instanceof(Accommodation, { message: '[Property] accommodation is required' }),
  geoCoordinate: z.instanceof(GeoCoordinate, { message: '[Property] geoCoordinate is required' }),
  address: z.instanceof(Address, { message: '[Property] address is required' }),
  city: z.instanceof(City, { message: '[Property] city is required' }),
  stars: z.instanceof(Stars, { message: '[Property] stars is required' }),
  numberOfComments: z.instanceof(NumberOfComments, { message: '[Property] numberOfComments is required' }), // prettier-ignore
  rating: z.instanceof(Rating).optional(),
  images: z.instanceof(ListOfImage, { message: '[Property] images is required' }),
  citySlug: z.instanceof(Slug, { message: '[Property] citySlug is required' }),
  price: z.instanceof(Price).optional(),
  facilities: z.instanceof(ListOfFacility).nullable().optional(),
  source: z.instanceof(PropertySource).optional(),
  highlighted: z.instanceof(PropertyHighlighted).optional(),
  distance: z.instanceof(DistanceToCityCenter).nullable().optional(),
  inCenter: z.boolean().default(false).optional(),
  nights: z.instanceof(Nights).nullable().optional(),
  isPrime: z.boolean().optional(),
})

export class Property extends Model {
  static create({
    id,
    name,
    country,
    slug,
    accommodation,
    geoCoordinate,
    address,
    city,
    stars,
    numberOfComments,
    rating,
    images,
    citySlug,
    price,
    facilities,
    source,
    highlighted,
    distance,
    inCenter,
    nights,
    isPrime,
  }: z.infer<typeof PropertyValidation>) {
    PropertyValidation.parse({
      id,
      name,
      country,
      slug,
      accommodation,
      geoCoordinate,
      address,
      city,
      stars,
      numberOfComments,
      rating,
      images,
      citySlug,
      price,
      facilities,
      source,
      highlighted,
      distance,
      inCenter,
      nights,
      isPrime,
    })

    return new Property(
      id,
      name,
      country,
      slug,
      accommodation,
      geoCoordinate,
      address,
      city,
      stars,
      numberOfComments,
      images,
      citySlug,
      rating,
      price,
      facilities ?? undefined,
      distance ?? undefined,
      inCenter,
      nights ?? undefined,
      source,
      highlighted,
      isPrime
    )
  }

  static fromRelatedAPI({
    json,
    affiliate,
    market,
  }: {
    json: POSTRelatedResponseType['properties'][number]
    affiliate: Affiliate
    market: Market
  }) {
    const distance = json.distance ? DistanceToCityCenter.createWithUnit(json.distance) : undefined

    return Property.create({
      id: ID.fromNumber({ value: json.id }),
      name: Name.create({ value: json.name }),
      country: Country.create({ value: json.property_location.country_code }),
      slug: Slug.create({ value: json.slug }),
      accommodation: Accommodation.create({ value: json.accommodation_type }),
      geoCoordinate: GeoCoordinate.create({
        latitude: json.property_location.coordinates.lat,
        longitude: json.property_location.coordinates.lon,
      }),
      address: Address.create({ value: json.address }),
      city: City.create({ value: json.property_location.city }),
      stars: Stars.create({ value: json.stars }),
      numberOfComments: NumberOfComments.create({ value: json.comments }),
      rating: Rating.create({ value: json.rating }),
      images: ListOfImage.fromRelatedAPI({ json: json.images }),
      citySlug: Slug.create({ value: json.property_location.city_slug }),
      price: Price.fromRelatedApi({ json, affiliate, market }),
      facilities: ListOfFacility.fromRelatedApi({ json: json.facilities }),
      distance,
      inCenter: distance?.isInCenter(),
    })
  }

  static fromBannerApi({
    json,
    affiliate,
    market,
  }: {
    json: POSTBannerResponseType['properties'][number]
    affiliate: Affiliate
    market: Market
  }) {
    const distance = json.distance ? DistanceToCityCenter.createWithUnit(json.distance) : undefined

    return Property.create({
      id: ID.fromNumber({ value: json.id }),
      name: Name.create({ value: json.name }),
      country: Country.create({ value: json.property_location.country_code }),
      slug: Slug.create({ value: json.slug }),
      accommodation: Accommodation.create({ value: json.accommodation_type }),
      geoCoordinate: GeoCoordinate.create({
        latitude: json.property_location.coordinates.lat,
        longitude: json.property_location.coordinates.lon,
      }),
      address: Address.create({ value: json.address }),
      city: City.create({ value: json.property_location.city }),
      stars: Stars.create({ value: json.stars }),
      numberOfComments: NumberOfComments.create({ value: json.comments }),
      rating: Rating.create({ value: json.rating }),
      images: ListOfImage.fromBannerApi({ json: json.images }),
      citySlug: Slug.create({ value: json.property_location.city_slug }),
      price: Price.fromBannerApi({ json, affiliate, market }),
      distance,
      inCenter: distance?.isInCenter(),
    })
  }

  static fromAPI({ json }: { json: GETpropertiesByIDsResponse['properties'][number] }) {
    const distance = json.distance
      ? DistanceToCityCenter.fromKM({ value: json.distance })
      : undefined

    return Property.create({
      id: ID.fromNumber({ value: json.id_property }),
      name: Name.create({ value: json.name }),
      country: Country.create({ value: json.country }),
      slug: Slug.create({ value: json.slug }),
      accommodation: Accommodation.create({ value: json.accommodation_type }),
      geoCoordinate: GeoCoordinate.create({ latitude: json.latitude, longitude: json.longitude }),
      address: Address.create({ value: json.address }),
      city: City.create({ value: json.city }),
      stars: Stars.create({ value: json.stars }),
      numberOfComments: NumberOfComments.createOrEmpty({ value: json.rating_count }),
      rating: Rating.createOrNull({ value: json.rating }) ?? undefined,
      images: ListOfImage.fromAPI({ json: json.images }),
      citySlug: Slug.create({ value: json.city_slug }),
      distance,
      inCenter: json.in_center ?? distance?.isInCenter(),
    })
  }

  static fromJSON({ json }: { json: ReturnType<Property['toJSON']> }) {
    return Property.create({
      id: ID.create({ value: json.id }),
      name: Name.create({ value: json.name }),
      country: Country.create({ value: json.country }),
      slug: Slug.create({ value: json.slug }),
      accommodation: Accommodation.create({ value: json.accommodation as AccommodationType }),
      geoCoordinate: GeoCoordinate.create(json.geoCoordinate),
      address: Address.create({ value: json.address }),
      city: City.create({ value: json.city }),
      stars: Stars.create({ value: json.stars }),
      numberOfComments: NumberOfComments.createOrEmpty({ value: json.numberOfComments }),
      rating: Rating.createOrNull({ value: json.rating }) ?? undefined,
      images: ListOfImage.fromJSON({ json: json.images }),
      citySlug: Slug.create({ value: json.citySlug }),
      price: json.price ? Price.fromJSON(json.price) : undefined,
      facilities: json.facilities ? ListOfFacility.fromJSON({ json: json.facilities }) : undefined,
      distance: json.distance ? DistanceToCityCenter.create({ value: json.distance }) : undefined,
      inCenter: json.inCenter,
      nights: json.nights ? Nights.create(json.nights) : undefined,
      source: json.source ? PropertySource.fromJSON(json.source) : undefined,
      highlighted: json.highlighted ? PropertyHighlighted.fromJSON(json.highlighted) : undefined,
      isPrime: json.isPrime,
    })
  }

  static fromPropertyV3API({ json }: { json: GetV3PropertyResponse }) {
    const citySlug = json.location?.city_slug || ''
    const distance = json.distance
      ? DistanceToCityCenter.fromKM({ value: json.distance })
      : undefined
    const ratingValue = json.ratings?.rating
    const numberOfComments = json.ratings?.rating_count ?? 0

    const facilitiesArray = json.facility_groups
      ? json.facility_groups.flatMap(group => group.facilities)
      : []

    return Property.create({
      id: ID.fromNumber({ value: json.id_property }),
      name: Name.create({ value: json.name }),
      country: Country.create({ value: json.country as Countries }),
      slug: Slug.create({ value: json.slug }),
      accommodation: Accommodation.create({
        value: json.accommodation_type as AccommodationType,
      }),
      geoCoordinate: GeoCoordinate.create({
        latitude: json.latitude,
        longitude: json.longitude,
      }),
      address: Address.create({ value: json.address }),
      city: City.create({ value: json.city }),
      stars: Stars.create({ value: json.stars }),
      numberOfComments: NumberOfComments.createOrEmpty({ value: numberOfComments }),
      rating: Rating.createOrNull({ value: ratingValue }) ?? undefined,
      images: ListOfImage.fromPropertyV3API({ json: json.images }),
      citySlug: Slug.create({ value: citySlug }),
      facilities:
        facilitiesArray.length > 0
          ? ListOfFacility.fromPropertyV3API({ json: facilitiesArray })
          : undefined,
      distance,
      inCenter: json.in_center ?? distance?.isInCenter(),
    })
  }

  static fromSerpApiLegacy({
    json,
    market,
    affiliate,
    source,
    highlighted,
  }: {
    json: SearchProperty
    market?: Market
    affiliate?: Affiliate
    source?: PropertySource
    highlighted?: PropertyHighlighted
  }) {
    const price = Price.fromSerpApiLegacyOrUndefined({ json, market, affiliate })

    const room0 = json.rooms?.[0]
    const isPrimeFromRoom = room0?.isPrime
    const isPrimeFromRateDiscounts = room0?.rateDiscounts?.prime
    const isPrime = Boolean(isPrimeFromRoom || isPrimeFromRateDiscounts || json.isPrime)

    return Property.create({
      id: ID.fromNumber({ value: json.id }),
      name: Name.create({ value: json.name }),
      country: Country.create({ value: json.country as Countries }),
      slug: Slug.create({ value: json.slug }),
      accommodation: Accommodation.create({
        value: json.accommodationType as AccommodationType,
      }),
      geoCoordinate: GeoCoordinate.create({
        latitude: json.latitude,
        longitude: json.longitude,
      }),
      address: Address.create({ value: json.address }),
      city: City.create({ value: json.city }),
      stars: Stars.create({ value: json.stars }),
      numberOfComments: NumberOfComments.createOrEmpty({ value: json.comments }),
      rating: Rating.createOrNull({ value: json.score }) ?? undefined,
      images: ListOfImage.fromSerpApiLegacy({ json: json.photos }),
      citySlug: Slug.create({ value: json.citySlug }),
      price,
      facilities: ListOfFacility.fromSerpApiLegacyOrEmpty({ json: json.facilities }),
      distance: DistanceToCityCenter.fromKmOrNull({ value: json.distance }),
      inCenter: Boolean(json.isInCenter),
      nights: Nights.create({ value: json.nights }),
      source,
      highlighted,
      isPrime,
    })
  }

  static fromJSONOrNull({ json }: { json: ReturnType<Property['toJSON']> }) {
    try {
      return Property.fromJSON({ json })
    } catch {
      return null
    }
  }

  constructor(
    public readonly _id: ID,
    public readonly _name: Name,
    public readonly _country: Country,
    public readonly _slug: Slug,
    public readonly _accommodation: Accommodation,
    public readonly _geoCoordinate: GeoCoordinate,
    public readonly _address: Address,
    public readonly _city: City,
    public readonly _stars: Stars,
    public readonly _numberOfComments: NumberOfComments,
    public readonly _images: ListOfImage,
    public readonly _citySlug: Slug,
    public readonly _rating?: Rating,
    public readonly _price?: Price,
    public readonly _facilities?: ListOfFacility,
    public readonly _distance?: DistanceToCityCenter,
    public readonly _inCenter?: boolean,
    public readonly _nights?: Nights,
    public readonly _source?: PropertySource,
    public readonly _highlighted?: PropertyHighlighted,
    public readonly _isPrime?: boolean
  ) {
    super()
  }

  get id() { return this._id.value } // prettier-ignore
  get name() { return this._name.value } // prettier-ignore
  get country() { return this._country.value } // prettier-ignore
  get slug() { return this._slug.value } // prettier-ignore
  get accommodation() { return this._accommodation.value } // prettier-ignore
  get geoCoordinate() { return this._geoCoordinate.toJSON() } // prettier-ignore
  get address() { return this._address.value } // prettier-ignore
  get city() { return this._city.value } // prettier-ignore
  get stars() { return this._stars.value } // prettier-ignore
  get distance() { return this._distance?.value } // prettier-ignore
  get inCenter() { return this._inCenter } // prettier-ignore
  get numberOfComments() { return this._numberOfComments.value } // prettier-ignore
  get rating() { return this._rating?.value } // prettier-ignore
  get images() { return this._images.toJSON() } // prettier-ignore
  get citySlug() { return this._citySlug.value } // prettier-ignore
  get price() { return this._price?.toJSON() } // prettier-ignore
  get facilities() { return this._facilities?.toJSON() } // prettier-ignore
  get nights() { return this._nights?.toJSON() } // prettier-ignore
  get source() { return this._source } // prettier-ignore
  get highlighted() { return this._highlighted } // prettier-ignore
  get isPrime() { return this._isPrime ?? false } // prettier-ignore

  toJSON() {
    return {
      id: this.id,
      name: this.name,
      country: this.country,
      slug: this.slug,
      accommodation: this.accommodation,
      geoCoordinate: this.geoCoordinate,
      address: this.address,
      city: this.city,
      stars: this.stars,
      ...(this.distance !== undefined && { distance: this.distance }),
      ...(this.inCenter !== undefined && { inCenter: this.inCenter }),
      numberOfComments: this.numberOfComments,
      rating: this.rating ?? null,
      images: this.images,
      citySlug: this.citySlug,
      url: this.url(),
      ...(this.price && { price: this.price }),
      ...(this.facilities && { facilities: this.facilities }),
      ...(this.nights && { nights: this.nights }),
      ...(this._source && { source: this._source.toJSON() }),
      ...(this._highlighted && { highlighted: this._highlighted.toJSON() }),
      ...(this._isPrime !== undefined && { isPrime: this._isPrime }),
    }
  }

  url(
    market?: Market,
    language?: Language,
    affiliateParams?: {
      aid?: string
      source?: string
      total?: string
      subtotal?: string
      taxes?: string
      offer_type?: string
    },
    partialSearch?: PartialSearch
  ) {
    const url = `/hotel/${this.country}/${this.slug}_${this.citySlug}`
    const queryParams = new URLSearchParams()
    queryParams.append('internal_navigation', 'true')

    if (market?.value) queryParams.append('market', market.value)
    if (language?.value) queryParams.append('lang', language.value)

    if (affiliateParams) {
      queryParams.append('aid', affiliateParams.aid ?? '')
      queryParams.append('source', affiliateParams.source ?? '')
      queryParams.append('total', affiliateParams.total ?? '')
      queryParams.append('subtotal', affiliateParams.subtotal ?? '')
      queryParams.append('taxes', affiliateParams.taxes ?? '')
      queryParams.append('offer_type', affiliateParams.offer_type ?? '')
    }

    if (partialSearch?.checkIn) queryParams.append('checkin', partialSearch?.checkIn)

    return `${url.toLowerCase()}?${queryParams.toString()}`
  }

  distanceByMarket({ market }: { market: Market }) {
    return this._distance?.byMarket({ market }) ?? null
  }

  imagesMap<T>(fn: (image: Image) => T): T[] {
    return this._images.map(image => fn(image))
  }

  percentageDiscount() {
    return this._price?.percentageDiscount()
  }

  hasRating(): this is Property & { rating: number } {
    return this._rating?.hasRating() ?? false
  }

  hasFacilities(): this is Property & { _facilities: NonEmptyArray<Facility> } {
    return Boolean(this._facilities?.hasFacilities())
  }

  isSoldOut(): boolean {
    if (!this._price && this._source) {
      return this._source.isNotVisited() && this._source.isNotHotelsSeen()
    }
    return false
  }

  isHighlighted(): boolean {
    return this._highlighted?.value() ?? false
  }

  isFoundOn({
    affiliate,
    source,
  }: {
    affiliate: Affiliate | undefined
    source: Source | undefined
  }): boolean {
    return (
      this.isHighlighted() &&
      Boolean(affiliate?.isMeta) &&
      Boolean(source) &&
      Boolean(source?.isNotDefault())
    )
  }

  facilitiesMap<T>(fn: (facility: Facility) => T): T[] {
    if (!this.hasFacilities()) {
      return []
    }
    return this._facilities.map(facility => fn(facility))
  }
}
